import { SerializedStyles, css } from '@emotion/react';
import styled from '@emotion/styled';
import { StyledHeading } from './HighlightTitle.styles';
import { COLORS3 as COLORS, FONT, MEDIA_QUERY, SPACING } from '../../styles/emotion';
import { FeaturedImageWrapper } from './FeaturedImage.styles';

const mastheadWrapperStyles = css`
  align-items: flex-start;
  border-bottom: var(--border-grid, 1px solid ${COLORS.BLACK_030});
  display: grid;
  flex-direction: row;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  height: fit-content;
  max-width: 100%;
  padding: ${SPACING.S32} 0;
  row-gap: ${SPACING.S32};
  overflow: hidden;

  ${MEDIA_QUERY.TABLET} {
    padding: ${SPACING.LG};
    row-gap: ${SPACING.LG};
  }

  ${MEDIA_QUERY.DESKTOP} {
    max-width: 1920px;
    padding: 0px;
    row-gap: 0;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    min-height: 455px;
  }

  ${MEDIA_QUERY.DESKTOP_XL} {
    min-height: 480px;
  }

  ${MEDIA_QUERY.DESKTOP_MASSIVE} {
    min-height: 600px;
  }
`;

const getPadding = (isPhotoOnRight?: boolean, removePadding?: boolean) => {
  if (removePadding) {
    return 0;
  }

  if (isPhotoOnRight) {
    return `${SPACING.LG} ${SPACING.LG} ${SPACING.LG} 0`;
  }

  return `${SPACING.LG} 0 ${SPACING.LG} ${SPACING.LG} `;
};

export const ImageWrapper = styled.div<{
  isPhotoOnRight?: boolean;
  removePadding?: boolean;
  hideImageNonDesktop: boolean;
}>`
  width: 100%;
  grid-row: 2;
  height: 100%;
  overflow: hidden;
  padding: 0 ${SPACING.S24};
  margin: 0 auto;
  max-height: 100%;

  ${MEDIA_QUERY.TABLET} {
    padding: 0;
  }

  ${MEDIA_QUERY.EXCLUDE_DESKTOP} {
    ${({ hideImageNonDesktop }) => hideImageNonDesktop && 'display: none;'}
  }

  ${MEDIA_QUERY.DESKTOP} {
    grid-row-start: 1;
    grid-row-end: span 2;
    padding: ${(props) => getPadding(props.isPhotoOnRight, props.removePadding)};
    border-width: ${(props) => (props.isPhotoOnRight ? '0 0 0 1px' : '0 1px 0 0')};
    min-height: 300px;
    grid-column: ${(props) => (props.isPhotoOnRight ? 2 : 1)};
    overflow: unset;
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    min-height: 375px;
  }

  ${FeaturedImageWrapper} {
    ${MEDIA_QUERY.TABLET} {
      height: inherit;
      min-height: 422px;
      max-height: 540px;
    }
    ${MEDIA_QUERY.MOBILE} {
      height: inherit;
      min-height: 200px;
      max-height: 422px;
    }
  }

  ${MEDIA_QUERY.DESKTOP_MASSIVE} {
    align-self: center;
  }
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;

  ${MEDIA_QUERY.DESKTOP} {
    padding: ${SPACING.LG};
    height: 100%;
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    padding: ${SPACING.LG} ${SPACING.XL};
  }
`;

export const SidebarContentWrapper = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-row: 1;
  padding: 0 ${SPACING.S24};

  ${MEDIA_QUERY.EXCLUDE_MOBILE} {
    padding: 0;
  }

  ${MEDIA_QUERY.TABLET} {
    grid-column: 1;
  }

  ${MEDIA_QUERY.DESKTOP} {
    h2 {
      font-size: ${FONT.FONT_SIZE_LARGER};
      line-height: 125%;
    }
  }
`;

export const ValueList = styled.ul`
  display: flex;
  flex-flow: column;
  row-gap: ${SPACING.XXS};
  padding: 0;
`;

export const ValueListItem = styled.li`
  display: flex;
  flex-flow: row;
`;

export const PropBullet = styled.div`
  align-self: flex-start;
  background-color: var(--text-primary, ${COLORS.BLACK_100});
  border-radius: 100%;
  display: inherit;
  flex-shrink: 1;
  height: 8px;
  margin-right: ${SPACING.XS};
  margin-top: ${SPACING.XS};
  width: 8px;

  ${MEDIA_QUERY.TABLET} {
    margin-top: 5.5px;
  }
`;

export const ValueProp = styled.h4`
  color: var(--text-primary);
  font-size: ${FONT.FONT_SIZE_SMALL};
  line-height: ${FONT.FONT_SIZE_LARGE};
  flex-shrink: 10;

  ${MEDIA_QUERY.TABLET} {
    font-size: ${FONT.FONT_SIZE_SMALL};
    line-height: ${FONT.FONT_SIZE_REGULAR};
  }
`;

export const TestimonialWrapper = styled.div`
  grid-column: 1;
  grid-row: 3;
  margin-top: auto;
  padding: 0 ${SPACING.S24};

  ${MEDIA_QUERY.TABLET} {
    padding: 0;
  }

  ${MEDIA_QUERY.DESKTOP} {
    grid-column: 2;
    grid-row: 2;
    padding: 0 ${SPACING.LG} ${SPACING.LG};
  }
`;

export const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${SPACING.XS};
  padding-top: ${SPACING.S32};
  ${MEDIA_QUERY.DESKTOP_LG} {
    column-gap: ${SPACING.XS};
  }

  ${MEDIA_QUERY.MOBILE} {
    flex-flow: wrap;
    row-gap: ${SPACING.XS};
    padding-top: ${SPACING.S24};
  }
`;

export const titleStyling = (): SerializedStyles => css`
  color: inherit;
  font-family: ${FONT.FONT_FAMILY_CIRCULAR};
  font-size: ${FONT.FONT_SIZE_LARGER};

  font-weight: ${FONT.FONT_WEIGHT_REGULAR};
  margin-top: 0;
  letter-spacing: -0.04em;
  line-height: 100%;
  display: inline;

  ${MEDIA_QUERY.TABLET} {
    font-size: 42px;
  }
  ${MEDIA_QUERY.DESKTOP_LG} {
    font-size: 42px;
  }

  ${MEDIA_QUERY.DESKTOP} {
    line-height: 125%;
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    line-height: 100%;
  }

  span,
  strong {
    font-family: ${FONT.FONT_FAMILY_NEW_SPIRIT};
    font-weight: ${FONT.FONT_WEIGHT_MEDIUM};
  }
`;

// maintain support for titles passed in as fragments
export const TitleWrapper = styled.span`
  ${titleStyling}
  padding-bottom: ${SPACING.S16};
  h1,
  h2 {
    ${titleStyling}
  }
`;

export const MastheadWrapper = styled.div`
  ${mastheadWrapperStyles}
`;

export const MastheadLinkWrapper = styled.a`
  ${mastheadWrapperStyles}
  text-decoration: none;

  @media (hover: hover) {
    img {
      transition: all 0.2s ease-in-out;
    }
    :hover {
      text-decoration: none;
      img {
        transform: scale(1.1);
      }
      ${TitleWrapper}, ${StyledHeading} {
        text-decoration: underline;
      }
    }
  }
`;
